/*** IMPORTS FROM imports-loader ***/
var define = false;

/*jslint browser: true, regexp: true, todo: true */

"use strict";

var ko = require("knockout");
var targetenv = require("targetenv");

var pli = require("../forms");
var knockoutUtil = require("../knockout/util");
var crumbIdUtil = require("../util/crumbId");
var reloadPageIfObsolete = require("../util/reloadPageIfObsolete");

var shared = require("./shared");


var viewModel = null;
var showSpinnerTimeout = null;
var creationContributors = []; // functions to be called immediately after 'ViewModelInit.createRoot'
var appliedContributors = []; // functions to be called immediately after 'ko.applyBindings' of pli.viewModel


//-------------------------------------------
function executeCallbacks(callbacks, viewModel, viewModelInit) {
    if (callbacks) {
        var i, l;
        for (i = 0, l = callbacks.length; i < l; i += 1) {
            callbacks[i](viewModel, viewModelInit);
        }
    }
}


//-------------------------------------------
// Process both successful and failing
// document load attempts.
function processLoadResponse(response) {
    var result = response.result,
        rootData = viewModel.extras.rootData || {};

    // check whether there is need to reload current page due to incompatible client and server versions
    if (reloadPageIfObsolete(response)) { return; }

    // cleanup
    rootData.call = null;
    if (showSpinnerTimeout !== null) { // in case load finished before progress was shown, cancel the pending timeout
        clearTimeout(showSpinnerTimeout);
        showSpinnerTimeout = null;
    }

    // process response
    if (console.group) { console.group("viewModel load response:", response); }
    if (response.isError) {
        if (console.warn) { console.warn("Document load failed:", response); }
        if (response.clientXhr && response.clientXhr.status === 403) { // "Forbidden" => we probably need to logon
            location.reload(true); // force reload should redirect us to logon page (and include current URL as return address); TODO: should be handled by Pli.call (which should show popup dialog instead of redirecting to login dialog)
        } else {
            rootData.value = result || { message: "Document could not be loaded" }; // used by progress indicator (failure subtitle)
            viewModel.extras.state("init.visible.failed");
        }
    } else {
        viewModel.load(result);
        if (response.clientXhr) { // if response was not immediate (i.e. server call), process the location hash again -- maybe we can get further this time if navigation pages were enabled; otherwise (immediate response) we are still in the middle of processing location hash and shouldn't re-process it
            pli.forms.onHashChange();
        }
        viewModel.extras.state("ready");
        pli.forms.resetActivationFocus();
    }
    if (console.groupEnd) { console.groupEnd(); }
}


//-------------------------------------------
// Load viewModel (as referenced by the module
// variable) from the specified record.
function loadViewModel(record) {
    var viewModelId;

    viewModel.extras.rootData.value = record;
    viewModel.extras.dirty.suspend();
    viewModel.loaded(record);

    // if viewModel ID changed, update URL hash
    viewModelId = (viewModel.id ? viewModel.id() : null);
    if (viewModelId && !crumbIdUtil.areEqual(viewModel.extras.rootData.id, viewModelId)) {
        viewModel.extras.rootData.id = viewModelId;
        viewModel.extras.rootForm.open({
            id: viewModelId,
            replaceId: true,
            replacePreservesCrumbsTail: true,
            includeInHash: true,
            doNotUpdateHistory: true
        });
        pli.forms.onHashChange(); // make sure crumbs are updated at this point
    }

    // resume and reset dirty flag tracking
    viewModel.extras.dirty.resume(true);
}


//---------------------------------------
// Save viewModel changes.
function saveViewModel() {
    var busyState = "busy.saving",
        rootForm = pli.forms.root,
        failedState = viewModel.extras.state.peek() + ".failed",
        useBusyIndicator = pli.forms.registry.busyIndicator && !viewModel.extras.suppressBusyIndicator;
    if (viewModel.extras.dirty() && shared.validateDeep(viewModel)) {
        if (viewModel.saving) { viewModel.saving(); }
        if (useBusyIndicator) {
            pli.forms.registry.busyIndicator.open({
                state: busyState,
                message: "Saving, please wait...",
                title: "Save in progress"
            });
        } else {
            viewModel.extras.state(busyState);
        }
        pli.call({ name: rootForm.saveCallName, jsonData: viewModel.extras.dirty.currentJSON() }).always(function processSaveResponse(response) {
            if (console.group) { console.group("viewModel save response:", response); }
            if (response.isError) {
                // if (console.warn) { console.warn("viewModel save failed"); }
                var validationErrors = response.result.validationErrors;
                if (useBusyIndicator) {
                    pli.forms.registry.busyIndicator.complete({
                        state: failedState,
                        failed: true,
                        message: response.result.message || "Unexpected save error",
                        title: "Save failed",
                        onClosed: (!validationErrors
                            ? null
                            : function () { shared.applyValidationErrors(viewModel, validationErrors); })
                    });
                } else {
                    shared.applyValidationErrors(viewModel, validationErrors);
                    viewModel.extras.state(failedState);
                }
            } else {
                if (useBusyIndicator) {
                    pli.forms.registry.busyIndicator.close({ // forced close
                        state: busyState,
                        force: true
                    });
                }
                viewModel.load(response.result);
                viewModel.extras.state("ready.saved");
            }
            if (console.groupEnd) { console.groupEnd(); }
        });
    }
    return false;
}



//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// ViewModelInit class (singleton)
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

//-------------------------------------------
// Create new instance of ViewModelInit class.
// Set properties to initial values, no other action.
function ViewModelInit() {
    this.createRoot = null; // how to create "root" viewmodel (Pli.viewModel); expected to be set to a valid function by a JavaScript component included after this one
    this.defaultKeymap = { // last-chance keyboard handling, can't be prevented
        "Esc": false,
        "F1": false,
        "F3": false,
        "F5": false
    };
    this.keymap = {}; // main keymap for rootForm
}


//-------------------------------------------
// Creates or updates viewmodel associated with the root crumb.
//
// Handles "pliInitializingFormData" event. If no viewmodel
// is active, a new instance is created using "createRoot",
// then initialized and a new async data load operation
// is started; if a viewmodel is already active, a new async
// load operation is started (assuming root document ID has changed).
ViewModelInit.prototype.setViewModelAsFormData = function setViewModelAsFormData(event) {
    var value, init = this;
    var rootForm = pli.forms.root;
    if (viewModel === null) { // is this the first time form is being initialized?
        // create viewmodel instance + give other scripts
        // a chance to initialize/extend it
        if (typeof init.createRoot !== "function") {
            throw new Error("ViewModelInit.setViewModelAsFormData: ViewModelInit.createRoot must be a valid function");
        }

        if (init.disableLegacyCallbacks) {
            if (creationContributors && creationContributors.length > 0) { throw new Error("Legacy callbacks are disabled, unable to register \"onViewModelCreated\""); }
            creationContributors = null;

            if (appliedContributors && appliedContributors.length > 0) { throw new Error("Legacy callbacks are disabled, unable to register \"onBindingsApplied\""); }
            appliedContributors = null;
        }

        var newViewModel = init.createRoot();
        if (!newViewModel) {
            throw new Error("ViewModelInit.setViewModelAsFormData: ViewModelInit.createRoot was expected to return an object");
        }
        pli.viewModel = viewModel = newViewModel;
        if (!viewModel.extras) {
            viewModel.extras = function ViewModelExtras() { return; };
        }
        if (!viewModel.extras.state) {
            viewModel.extras.state = shared.createViewModelState();
            viewModel.extras.state.syncWith("state");
        }
        viewModel.extras.rootForm = rootForm;
        viewModel.extras.rootData = null;
        viewModel.load = loadViewModel;
        viewModel.save = saveViewModel;
        executeCallbacks(creationContributors, viewModel, init);
        creationContributors = null;
        if (typeof viewModel.created === "function") {
            viewModel.created(init);
        }
        if (!viewModel.extras.dirty) { // initialize dirty tracking as the last thing to happen on viewmodel before binding (if not yet initialized)
            viewModel.extras.dirty = knockoutUtil.dirtyFlag(viewModel, true, false); // create suspended, non-persistent dirty flag
        }

        // register root & default keymaps
        rootForm.page.keymap = rootForm.id;
        if (typeof viewModel.createKeymapSpec === "function") {
            var keymapSpec = viewModel.createKeymapSpec();
            if (keymapSpec) {
                pli.keys.registerKeymap(rootForm.id, keymapSpec.formKeymap);
                pli.keys.registerKeymap("default", keymapSpec.defaultKeymap);
                pli.keys.setSchema(keymapSpec.formSchema);
            }
        } else {
            pli.keys.registerKeymap(rootForm.id, init.keymap);
            pli.keys.registerKeymap("default", init.defaultKeymap);
        }
        pli.viewModelInit = null; // only reset for backward compatiblity

        // connect (bind) viewModel to HTML
        if (targetenv.isBrowser) {
            ko.applyBindings(viewModel);
        }

        executeCallbacks(appliedContributors, viewModel, init);
        appliedContributors = null;
        if (typeof viewModel.appliedBindings === "function") {
            viewModel.appliedBindings(init);
        }
    } else { // nope, form data has been initialized before (just reset state)
        if (viewModel.extras.rootData) {
            if (crumbIdUtil.areEqual(viewModel.extras.rootData.id, event.pli.data.id)) { // we are just updating URL hash after updating viewModel.id
                event.pli.data.value = viewModel.extras.rootData.value;
                event.pli.data.form = viewModel.extras.rootData.form;
                viewModel.extras.rootData = event.pli.data;
                return viewModel;
            }
            viewModel.extras.rootData.value = null;
        }
        viewModel.extras.state("init");
    }

    // kickstart data loading (if needed)
    viewModel.extras.rootData = event.pli.data;
    if (typeof viewModel.loading === "function") {
        value = viewModel.loading(event.pli.data);
    }
    if (value) {
        processLoadResponse({ result: value });
    } else {
        if (typeof rootForm.loadCallName !== "string") {
            processLoadResponse({ isError: true, result: { message: "Expected string property \"rootForm.loadCallName\" (name of PLI call to load document data)" } });
        } else if (typeof rootForm.saveCallName !== "string") {
            processLoadResponse({ isError: true, result: { message: "Expected string property \"rootForm.saveCallName\" (name of PLI call to save document data)" } });
        } else {
            showSpinnerTimeout = setTimeout(function () {
                showSpinnerTimeout = null;
                viewModel.extras.state("init.visible");
            }, 250);

            var loadData = viewModel.extras.rootData.loadData;
            if (loadData) {
                viewModel.extras.rootData.loadData = null;
            } else {
                loadData = { id: viewModel.extras.rootData.id };
            }
            event.pli.data.call = pli.call({
                name: rootForm.loadCallName,
                data: loadData,
                deferSignInUiClosingUntil: viewModel.extras.state && viewModel.extras.state.isLoaded
            }).always(processLoadResponse);
        }
    }

    return viewModel;
};


//-------------------------------------------
// Obsolete, do not use (use require("shared").applyValidationErrors instead).
ViewModelInit.prototype.applyValidationErrors = function (validationErrors) {
    shared.applyValidationErrors(this, validationErrors);
};


//-------------------------------------------
// Obsolete, do not use (use direct require/import instead).
ViewModelInit.prototype.createGridViewModelFactory = require("./gridViewModel");


//-------------------------------------------
// Flag that controls support for legacy callbacks
// ("onBindingsApplied" and "onViewModelCreated").
// When false (default), callbacks can be used.
// When true, all attempts to register legacy
// callbacks will throw an exception.
// It is strongly recommended that all new code
// disables legacy callbacks.
ViewModelInit.prototype.disableLegacyCallbacks = false;


//-------------------------------------------
// Obsolete, do not use (use direct require/import instead).
ViewModelInit.prototype.enableSlideTransitions = require("./slideTransition");


//-------------------------------------------
// Register a new callback to be called after
// ko.applyBindings is called for viewModel.
// Callbacks will be called in the order in
// which they were registered.
ViewModelInit.prototype.onBindingsApplied = function (callback) {
    if (appliedContributors === null) { throw new Error("Legacy callbacks are disabled, unable to register \"onBindingsApplied\""); }
    appliedContributors.push(callback);
};


//-------------------------------------------
// Register a new callback to be called after
// viewmodel is created. Callbacks will be called
// in the order in which they were registered.
ViewModelInit.prototype.onViewModelCreated = function (callback) {
    if (creationContributors === null) { throw new Error("Legacy callbacks are disabled, unable to register \"onViewModelCreated\""); }
    creationContributors.push(callback);
};


//-------------------------------------------
// Resets "ViewModelInit" environment to be able to create a new root viewmodel.
ViewModelInit.prototype.reset = function resetViewModelInit(viewModelSpec) {
    // cancel async operations (that we know of)
    if (showSpinnerTimeout !== null) {
        clearTimeout(showSpinnerTimeout);
        showSpinnerTimeout = null;
    }

    viewModel = null;
    creationContributors = null;
    appliedContributors = null;

    pli.viewModel = null;
    pli.viewModelInit = this;

    var lastViewModelSpec = { createRoot: this.createRoot, disableLegacyCallbacks: this.disableLegacyCallbacks };

    if (viewModelSpec) {
        this.disableLegacyCallbacks = !!(viewModelSpec.disableLegacyCallbacks);
        if (viewModelSpec.rootViewModelClass) {
            this.createRoot = function createRootViewModelFromClass() { return new viewModelSpec.rootViewModelClass(); };
        } else if (viewModelSpec.createRoot) {
            this.createRoot = viewModelSpec.createRoot;
        }
    } else {
        this.createRoot = null;
        this.disableLegacyCallbacks = false;
    }
    return lastViewModelSpec;
};


//-------------------------------------------
// Obsolete, do not use (use require("shared").computeErrorsCount instead).
ViewModelInit.prototype.computeErrorsCount = function () {
    return shared.computeErrorsCount(this);
};


//-------------------------------------------
// Obsolete, do not use (use require("shared").validateDeep instead).
ViewModelInit.prototype.validateDeep = function () {
    return shared.validateDeep(this);
};



//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Initialization
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

var viewModelInit = new ViewModelInit();
if (targetenv.isBrowser) {
    // set viewmodel as root form data (triggered for the first time just after scripts are loaded and Pli.forms (navigation) module finishes its initialization)
    pli.forms.root.$form.on("pliInitializingFormData", function initializeFormData(event) {
        if (event.pli.data.form === pli.forms.root) { // ignore pliInitializingFormData events for non-root (e.g. nested) forms
            viewModelInit.setViewModelAsFormData(event);
            return false; // mark this event as handled
        }
    });
}

module.exports = viewModelInit;




//////////////////
// WEBPACK FOOTER
// ./~/pli/forms-viewmodel/viewModelInit.js
// module id = 466
// module chunks = 49